import {useLingui} from "@lingui/react";
import {Trans} from "@lingui/react/macro";
import {useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router";
import createId, {IdType} from "../../models/create-id";
import type {
  GroupRepo,
  IngredientEntry,
  IngredientId,
  ItemEntry,
  ItemId,
  RecipeEntry,
} from "../../models/GroupRepo";
import {useSortedIngredients} from "../../models/recipe-queries";
import PlusMinusButtons from "../../shared/PlusMinusButtons";
import {blueTheme, greenTheme, tealTheme} from "../../styles/themes/color-themes.css";
import {Box, Col, css, Row} from "../../ui/Box";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import {Input} from "../../ui/Input";
import {createSorter} from "../../utils/sort-utils";
import {useSuggestions} from "../items/AddItem";
import {ItemPillLayout, MiniPill, QuantityPill} from "../items/ItemPill";
import RefineIngredient from "./RefineIngredient";

const AddVariant = ({
  item,
  onAdd,
  hasVariantsAlready,
}: {
  item: ItemEntry;
  groupRepo: GroupRepo;
  onAdd: (item: ItemEntry) => void;
  hasVariantsAlready: boolean;
}) => {
  return (
    <ItemPillLayout
      emoji={item.emoji}
      theme={blueTheme}
      name={item.name}
      onClick={() => onAdd(item)}
      confirmAnimation="added"
    >
      {hasVariantsAlready ? (
        <Row align="center" pr="4">
          <MiniPill>
            <Trans>Add new variant</Trans>
          </MiniPill>
        </Row>
      ) : null}
    </ItemPillLayout>
  );
};

const ShowVariant = ({
  ingredient,
  groupRepo,
  item,
}: {
  ingredient: IngredientEntry;
  groupRepo: GroupRepo;
  item: ItemEntry;
}) => {
  const handleUpdate = (next: number) => {
    groupRepo.Ingredients.patch(ingredient.id, {quantity: next});
  };
  const handleDelete = () => {
    groupRepo.Ingredients.delete(ingredient.id);
  };
  return (
    <ItemPillLayout emoji={item.emoji} theme={tealTheme} name={item.name} confirmAnimation="added">
      <Row flex="none" relative>
        <Row wrap sp="4" absolute justify="end" bottom="0" right="100%" px="4" pb="4">
          <QuantityPill variant={ingredient} />
          {ingredient.variant && <MiniPill>{ingredient.variant}</MiniPill>}
        </Row>
        <PlusMinusButtons
          value={ingredient.quantity}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      </Row>
    </ItemPillLayout>
  );
};

const ShowItem = ({
  item,
  onAdd,
  groupRepo,
  ingredients,
}: {
  item: ItemEntry;
  onAdd: (item: ItemEntry) => void;
  groupRepo: GroupRepo;
  ingredients: IngredientEntry[] | null;
}) => {
  return (
    <>
      {ingredients?.map((ingredient) => (
        <ShowVariant
          ingredient={ingredient}
          item={item}
          key={ingredient.id}
          groupRepo={groupRepo}
        />
      ))}
      <AddVariant
        item={item}
        groupRepo={groupRepo}
        onAdd={onAdd}
        hasVariantsAlready={ingredients !== null}
      />
    </>
  );
};

type CreateItemPillProps = {
  name: string;
  groupRepo: GroupRepo;
  onAdd: (item: ItemEntry) => void;
};
export const CreateItemPill = (props: CreateItemPillProps) => {
  const {name, groupRepo, onAdd} = props;
  const handleAddItem = () => {
    const itemId = createId(IdType.Item);
    const item: ItemEntry = {
      id: itemId,
      name,
      createdAt: new Date(),
      categoryId: null,
      emoji: null,
    };
    groupRepo.Items.set(itemId, item);
    onAdd(item);
  };
  return (
    <ItemPillLayout
      onClick={handleAddItem}
      emoji={<Icon name="plus" />}
      theme={greenTheme}
      name={name}
      confirmAnimation="added"
    />
  );
};

type SuggestionsProps = {
  searchVal: string;
  onAdd: (item: ItemEntry) => void;
  groupRepo: GroupRepo;
  ingredientsByItemId: Map<ItemId, IngredientEntry[]>;
  items: Record<ItemId, ItemEntry>;
};
const Suggestions = ({
  searchVal,
  onAdd,
  groupRepo,
  ingredientsByItemId,
  items: passedItems,
}: SuggestionsProps) => {
  const items = Object.values(passedItems);
  const rawSuggs = useSuggestions(searchVal.trimStart(), items, (i) => i.name);
  const shownSuggs = !searchVal ? items : rawSuggs;
  const suggs = shownSuggs.length > 15 ? shownSuggs.slice(0, 15) : shownSuggs;

  const shouldShowAddEl = () => {
    if (!searchVal.trim()) return false;
    if (suggs.length === 0) return true;
    const firstRes = suggs[0];
    return searchVal.trim().toLowerCase() !== firstRes.name.toLowerCase();
  };

  return (
    <Box overflow="auto" flex="auto">
      <Col sp="8">
        {suggs.map((item) => (
          <ShowItem
            key={item.id}
            item={item}
            onAdd={(item) => onAdd(item)}
            groupRepo={groupRepo}
            ingredients={ingredientsByItemId.get(item.id) ?? null}
          />
        ))}
        {shouldShowAddEl() && (
          <CreateItemPill name={searchVal} onAdd={onAdd} groupRepo={groupRepo} />
        )}
      </Col>
    </Box>
  );
};

type UpdateIngredientsProps = {
  recipe: RecipeEntry;
  groupRepo: GroupRepo;
};
const AddIngredient = ({recipe, groupRepo}: UpdateIngredientsProps) => {
  const [searchVal, setSearchVal] = useState("");
  const [refineIngredientId, setRefineIngredientId] = useState<IngredientId | null>(null);
  const searchFieldRef = useRef<HTMLInputElement | null>(null);
  const ingredients = useSortedIngredients(groupRepo, recipe.id);
  const items = groupRepo.Items.useGetAll();

  const handleInput = (e: string) => {
    if (refineIngredientId) setRefineIngredientId(null);
    setSearchVal(e);
  };

  const handleSelected = (ingredientId: IngredientId) => {
    setSearchVal("");
    searchFieldRef.current?.focus();
    setRefineIngredientId(ingredientId);
  };

  const handleAdd = (item: ItemEntry) => {
    const id = createId(IdType.Ingredient);
    const lastIng = ingredients.at(-1);
    const sortIndex = createSorter().addAtEnd({
      currLastValue: lastIng ? lastIng.sortIndex : null,
      expectedRemainingCount: Math.max(5, 20 - ingredients.length),
    });
    groupRepo.Ingredients.set(id, {
      id: id,
      recipeId: recipe.id,
      itemId: item.id,
      variant: null,
      quantity: 1,
      unit: null,
      sortIndex,
    });
    handleSelected(id);
  };

  const {i18n} = useLingui();
  const navigate = useNavigate();
  const onClose = () => navigate("..");

  const ingredientsByItemId = useMemo(() => {
    const map = new Map<ItemId, IngredientEntry[]>();
    for (const ingredient of ingredients) {
      const existing = map.get(ingredient.itemId);
      if (existing) {
        existing.push(ingredient);
      } else {
        map.set(ingredient.itemId, [ingredient]);
      }
    }
    return map;
  }, [ingredients]);

  return (
    <Col
      sp="24"
      height="_addItemDrawerHeight"
      maxWidth="formWidth"
      width="100%"
      mr="auto"
      ml="auto"
    >
      <Row sp="8">
        <Input
          type="text"
          value={searchVal}
          onChange={handleInput}
          placeholder={i18n.t({id: "input.addIngredientPlaceholder"})}
          ref={searchFieldRef}
          className={css({flex: "auto"})}
          data-drawer-autofocus
        />
        <Button onClick={onClose} variant="tertiary">
          <Icon name="close" />
        </Button>
      </Row>

      {refineIngredientId ? (
        <RefineIngredient
          id={refineIngredientId}
          onClose={() => setRefineIngredientId(null)}
          groupRepo={groupRepo}
        />
      ) : (
        <Suggestions
          searchVal={searchVal}
          onAdd={handleAdd}
          groupRepo={groupRepo}
          ingredientsByItemId={ingredientsByItemId}
          items={items}
        />
      )}
    </Col>
  );
};

export default AddIngredient;
